import {useEffect, useMemo, useState} from "react";
import {toast} from "sonner";
import {Card} from "@/components/ui/card.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Switch} from "@/components/ui/switch.tsx";
import {LoadingButton} from "@/components/ui/button.tsx";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {Spinner} from "@/components/ui/spinner.tsx";
import {AudioPickerField} from "@/pages/sclass-test/components/AudioPickerField.tsx";
import {SClassTest} from "@/types/SClassTest.ts";
import {useGetSClassOptionsQuery, useUpdateSClassTestMutation} from "@/store/sclassTest.ts";
import {formatApiError} from "@/pages/sclass-test/apiError.ts";

function toInputDateTime(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function buildForm(test: SClassTest) {
    return {
        description: test.description || '',
        sclass: test.sclass,
        field_speach_id: test.field_speach_id,
        start_date: toInputDateTime(test.start_date),
        end_date: toInputDateTime(test.end_date),
        is_active: test.is_active,
        here_score: test.here_score,
        max_score: test.max_score?.toString() ?? '',
    };
}

type TestDetailsCardProps = {
    test: SClassTest;
    onDirtyChange?: (isDirty: boolean) => void;
};

export function TestDetailsCard({test, onDirtyChange}: TestDetailsCardProps) {
    const [updateTest, {isLoading}] = useUpdateSClassTestMutation();
    const {data: sclassOptions, isLoading: isLoadingSclassOptions} = useGetSClassOptionsQuery();

    const [form, setForm] = useState(() => buildForm(test));
    const [baseline, setBaseline] = useState(() => buildForm(test));

    useEffect(() => {
        const next = buildForm(test);
        setForm(next);
        setBaseline(next);
    }, [test.id]);

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    const patch = (p: Partial<typeof form>) => setForm(prev => ({...prev, ...p}));

    async function handleSave() {
        try {
            await updateTest({
                id: test.id,
                sclass: form.sclass,
                description: form.description,
                field_speach_id: form.field_speach_id,
                start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
                end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
                is_active: form.is_active,
                here_score: form.here_score,
                max_score: form.max_score ? Number(form.max_score) : null,
            }).unwrap();
            setBaseline(form);
            toast.success('מקבץ השאלות עודכן בהצלחה');
        } catch (e: any) {
            toast.error('שגיאה בשמירה', {description: formatApiError(e)});
        }
    }

    return (
        <Card className="p-6 space-y-5">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-neutral-800">
                    פרטי מקבץ השאלות
                </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-1 sm:col-span-2">
                    <Label>שם מקבץ השאלות</Label>
                    <Input value={form.description} onChange={(e) => patch({description: e.target.value})}
                           placeholder="לדוגמה: מבחן שבועי"/>
                </div>

                <div className="space-y-1">
                    <Label>שלוחה</Label>
                    <Select
                        value={form.sclass ? String(form.sclass) : undefined}
                        onValueChange={(v) => patch({sclass: v ? Number(v) : null})}
                        disabled={isLoadingSclassOptions}
                    >
                        <SelectTrigger className="w-full">
                            {isLoadingSclassOptions ? <Spinner size="sm"/> : <SelectValue placeholder="בחר שלוחה"/>}
                        </SelectTrigger>
                        <SelectContent>
                            {sclassOptions?.map((opt) => (
                                <SelectItem key={opt.id} value={String(opt.id)}>{opt.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <AudioPickerField label="הקלטת שם המבחן" value={form.field_speach_id}
                                  onChange={(id) => patch({field_speach_id: id})}/>

                <div className="space-y-1">
                    <Label>מגבלת צבירת נקודות</Label>
                    <Input type="number" value={form.max_score} onChange={(e) => patch({max_score: e.target.value})}
                           placeholder="ללא הגבלה"/>
                </div>

                <div className="space-y-1">
                    <Label>תאריך פתיחת המבחן</Label>
                    <Input type="datetime-local" value={form.start_date}
                           onChange={(e) => patch({start_date: e.target.value})}/>
                </div>

                <div className="space-y-1">
                    <Label>תאריך סגירת המבחן</Label>
                    <Input type="datetime-local" value={form.end_date}
                           onChange={(e) => patch({end_date: e.target.value})}/>
                </div>

                <div className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2">
                    <Label className="cursor-pointer">מקבץ שאלות פעיל</Label>
                    <Switch checked={form.is_active} onCheckedChange={(v) => patch({is_active: v})}/>
                </div>

                <div className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2">
                    <Label className="cursor-pointer">ניתן לשמוע ציונים</Label>
                    <Switch checked={form.here_score} onCheckedChange={(v) => patch({here_score: v})}/>
                </div>
            </div>

            <div className="flex items-center justify-end gap-3">
                {isDirty && <span className="text-xs text-amber-600">יש שינויים שלא נשמרו</span>}
                <LoadingButton type="button" isLoading={isLoading} onClick={handleSave}>
                    שמירת פרטי המקבץ
                </LoadingButton>
            </div>
        </Card>
    );
}

